import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { useReturnViewState, useRestoreScroll, PURCHASES_FLOW_PREFIXES, COMPLETED_PURCHASES_VIEW_KEY } from '../hooks/useReturnViewState';
import Footer from '../components/Footer';
import { useNotifications } from '../contexts/NotificationContext';
import { placeholderFor } from '../utils/listingPlaceholder';

const isMessagingAvailable = (purchase) => {
  const completionTimestamp = purchase.autoCompletedAt || purchase.completedAt;
  
  if (!completionTimestamp) {
    return false;
  }
  
  const completedDate = completionTimestamp.toDate ? completionTimestamp.toDate() : new Date(completionTimestamp);
  const now = new Date();
  const daysSinceCompletion = (now - completedDate) / (1000 * 60 * 60 * 24);
  
  return daysSinceCompletion <= 30;
};

const getUserPurchaseStep = (purchase, currentUserId) => {
  const isBuyer = purchase.initiatorId === currentUserId;
  const shipping = purchase.shippingStatus || {};
  
  // For purchase offers, only the seller has shipping steps
  const userShipping = isBuyer ? null : shipping.seller;
  
  // Check if user has provided feedback
  const userFeedback = isBuyer ? purchase.buyerFeedback : purchase.sellerFeedback;
  const hasProvidedFeedback = userFeedback && userFeedback.submittedAt;
  
  const user = {
    paid: isBuyer ? (purchase.paymentStatus?.buyerPaid || false) : (purchase.paymentStatus?.sellerPaid || false),
    photoUploaded: isBuyer ? false : (shipping.confirmationPhoto ? true : false),
    ready: isBuyer ? false : shipping.sellerReady,
    label: isBuyer ? false : (shipping.labelCreated || false),
    inTransit: shipping.inTransit || false,
    delivered: shipping.delivered || false,
  };

  // If purchase is complete and user has provided feedback, show completed status
  if (purchase.status === 'complete' && hasProvidedFeedback) {
    return 'Purchase Completed';
  }
  
  // If purchase is complete but no feedback yet, show delivered status
  if (purchase.status === 'complete' && user.delivered) {
    return 'Delivered - Feedback Pending';
  }
  
  const steps = [
    { label: 'Completed Payment', check: (u) => u.paid },
    { label: 'Upload Confirmation Photo', check: (u) => u.photoUploaded },
    { label: 'Ready to Ship', check: (u) => u.ready },
    { label: 'Generate Shipping Label', check: (u) => u.label },
    // In Transit completes on delivery; the separate delivery step was folded
    // into it on the progress pages (2026-11-01). Same ladder here.
    { label: 'Item In Transit', check: (u) => u.delivered },
    { label: 'Feedback Provided', check: () => hasProvidedFeedback },
  ];

  // For buyers, only show payment step
  if (isBuyer) {
    return user.paid ? 'Payment Completed' : 'Payment Pending';
  }

  // For sellers, show their progress
  const completedIndex = steps.findIndex((step) => !step.check(user));
  return completedIndex === -1 ? 'Purchase Completed' : steps[completedIndex].label;
};

const CompletedPurchases = () => {
  const navigate = useNavigate();
  const { notifications, markAsRead } = useNotifications();
  const claimedNotificationIds = useRef(new Set());

  // The completed-purchase alert also clears from the purchase's progress page,
  // but this list is where the "Purchases > Completed" tab lands. Every
  // completed purchase is rendered here, so anything unread is on screen.
  useEffect(() => {
    const pending = notifications.filter(
      n => n.type === 'purchase_complete' && !n.read && !claimedNotificationIds.current.has(n.id)
    );
    if (pending.length === 0) return;

    // Claim before awaiting — see the note in TradeCenter; each write pushes a
    // snapshot that re-runs this effect mid-loop.
    pending.forEach(n => claimedNotificationIds.current.add(n.id));

    (async () => {
      console.log(`📧 Marking ${pending.length} purchase completion notification(s) as read on CompletedPurchases page`);
      for (const notification of pending) {
        await markAsRead(notification.id);
      }
    })();
  }, [notifications, markAsRead]);

  const [completedPurchases, setCompletedPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  // "Put me back where I was": capture-handler save + anchored restore, same
  // rationale as AcceptedTrades (see hooks/useReturnViewState.js).
  const { scrollSnapshot, saveView } = useReturnViewState({
    storageKey: COMPLETED_PURCHASES_VIEW_KEY,
    flowPrefixes: PURCHASES_FLOW_PREFIXES,
  });
  useRestoreScroll(scrollSnapshot, !loading);
  const [usernames, setUsernames] = useState({});
  const [userProfiles, setUserProfiles] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        setIsHeaderCompact(y > 400);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, notLoggedIn]);

  const headerLinkBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    border: '1px solid #C89F3C',
    backgroundColor: 'white',
    color: '#374151',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '0.875rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const renderMobileChromeBar = () => (
    <div className="cp-compact-bar-inner">
      <span className="cp-compact-title">Completed Purchases</span>
      <span className="cp-compact-spacer" aria-hidden="true" />
      <Link to="/accepted-purchases" className="cp-link-btn" style={headerLinkBtnStyle}>
        Accepted Purchases
      </Link>
    </div>
  );

  const getMembershipBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            9V Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            18V Member
          </div>
        );
      default:
        return null;
    }
  };

  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  const requestedUsernameIdsRef = useRef(new Set());
  const requestedProfileIdsRef = useRef(new Set());
  const fetchUsernames = async (purchasesList) => {
    // Get both initiatorId and responderId from all purchases
    const initiatorIds = purchasesList.map(p => p.initiatorId).filter(Boolean);
    const responderIds = purchasesList.map(p => p.responderId).filter(Boolean);
    
    // Also include current user ID to fetch their username
    const currentUserId = auth.currentUser?.uid;
    const allUserIds = [...initiatorIds, ...responderIds];
    if (currentUserId && !allUserIds.includes(currentUserId)) {
      allUserIds.push(currentUserId);
    }
    
    const uniqueUserIds = [...new Set(allUserIds)];
    console.log('🔍 CompletedPurchases - Fetching usernames for:', uniqueUserIds);
    const newUsernames = {};

    // Parallel, and each profile fetched once per page life: same treatment
    // as the Offers hub (8f972cf). The old serial loop's "already fetched?"
    // check read stale closure state, so every live-listener fire re-fetched
    // every profile one at a time. (fetchUserProfiles below reads the same
    // docs for its richer shape; it keeps its own fetched-once set, so the
    // duplication costs one cheap parallel batch instead of two serial ones.)
    const toFetch = uniqueUserIds.filter((uid) => !requestedUsernameIdsRef.current.has(uid));
    if (toFetch.length === 0) return;
    toFetch.forEach((uid) => requestedUsernameIdsRef.current.add(uid));

    const results = await Promise.all(toFetch.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'publicProfiles', uid));
        return [uid, userDoc.exists() ? userDoc.data().username || 'Unknown' : 'Unknown'];
      } catch (err) {
        console.error('Error fetching username for', uid, err);
        requestedUsernameIdsRef.current.delete(uid); // let a later update retry
        return [uid, 'Unknown'];
      }
    }));

    Object.assign(newUsernames, Object.fromEntries(results));
    setUsernames((prev) => ({...prev, ...newUsernames}));
  };

  const fetchUserProfiles = async (purchasesList) => {
    // Get all user IDs from purchases
    const initiatorIds = purchasesList.map(p => p.initiatorId).filter(Boolean);
    const responderIds = purchasesList.map(p => p.responderId).filter(Boolean);
    const currentUserId = auth.currentUser?.uid;
    const allUserIds = [...initiatorIds, ...responderIds];
    if (currentUserId && !allUserIds.includes(currentUserId)) {
      allUserIds.push(currentUserId);
    }
    
    const uniqueUserIds = [...new Set(allUserIds)];
    console.log('🔍 CompletedPurchases - Fetching profiles for:', uniqueUserIds);
    const newUserProfiles = {};

    // Same parallel/fetched-once treatment as fetchUsernames above.
    const toFetch = uniqueUserIds.filter((uid) => !requestedProfileIdsRef.current.has(uid));
    if (toFetch.length === 0) return;
    toFetch.forEach((uid) => requestedProfileIdsRef.current.add(uid));

    const results = await Promise.all(toFetch.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'publicProfiles', uid));
        if (!userDoc.exists()) return null;
        const userData = userDoc.data();
        return [uid, {
          username: userData.username || 'Unknown',
          photoURL: userData.photoURL || '/images/default-avatar.png',
          membershipTier: userData.membershipTier || 'free',
          badge: userData.badge || 0,
          averageRating: userData.averageRating || 0,
        }];
      } catch (err) {
        console.error('Error fetching profile for', uid, err);
        requestedProfileIdsRef.current.delete(uid); // let a later update retry
        return null;
      }
    }));

    Object.assign(newUserProfiles, Object.fromEntries(results.filter(Boolean)));
    setUserProfiles((prev) => ({...prev, ...newUserProfiles}));
  };

  useEffect(() => {
    let unsubscribeBuyer;
    let unsubscribeSeller;
    let unsubscribeAuth;
    
    unsubscribeAuth = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (!user) { setNotLoggedIn(true); setLoading(false); return; }
      const userId = user.uid;

      // Query for completed purchases where user is the buyer (initiator)
      const buyerQuery = query(
        collection(db, 'purchaseOffers'),
        where('status', 'in', ['complete', 'completed']),
        where('initiatorId', '==', userId)
      );
      
      // Query for completed purchases where user is the seller (responder)
      const sellerQuery = query(
        collection(db, 'purchaseOffers'),
        where('status', 'in', ['complete', 'completed']),
        where('responderId', '==', userId)
      );

      // Listen to both queries and combine results
      unsubscribeBuyer = onSnapshot(buyerQuery, async (buyerSnapshot) => {
        unsubscribeSeller = onSnapshot(sellerQuery, async (sellerSnapshot) => {
          console.log('🔍 CompletedPurchases - Buyer purchases:', buyerSnapshot.docs.length);
          console.log('🔍 CompletedPurchases - Seller purchases:', sellerSnapshot.docs.length);
          
          // Combine both snapshots
          const allDocs = [...buyerSnapshot.docs, ...sellerSnapshot.docs];
          console.log('🔍 CompletedPurchases - Total completed purchases:', allDocs.length);
          
          const purchases = await Promise.all(allDocs.map(async docSnap => {
            const purchase = { id: docSnap.id, ...docSnap.data() };
            
            // 📸 PURCHASE HISTORY PRESERVATION: Use pedal snapshot if available
            // This prevents completed purchase images from changing when buyer reactivates the pedal
            let targetPedal = null;
            
            if (purchase.pedalSnapshot?.targetPedal) {
              console.log('📸 Using pedal snapshot for completed purchase display');
              targetPedal = purchase.pedalSnapshot.targetPedal;
            } else {
              // Fallback to live data for older purchases without snapshots
              console.log('🔄 Fetching live pedal data for older completed purchase');
              const targetPedalSnap = await getDoc(doc(db, 'listings', purchase.targetPedalId));
              targetPedal = targetPedalSnap.exists() ? targetPedalSnap.data() : null;
            }
            
            const isBuyer = user?.uid === purchase.initiatorId;
            const myPedal = isBuyer ? null : targetPedal; // Buyer has no pedal, seller has the pedal
            const theirPedal = isBuyer ? targetPedal : null; // Buyer sees seller's pedal, seller sees buyer's info
            
            return { ...purchase, myPedal, theirPedal, targetPedal };
          }));
          
          // Sort by acceptance date (from history), fallback to createdAt
          purchases.sort((a, b) => {
            // Find the acceptance timestamp from history
            const getAcceptanceDate = (purchase) => {
              if (purchase.history && Array.isArray(purchase.history)) {
                const acceptedEntry = [...purchase.history].reverse().find(h => 
                  h.message && h.message.toLowerCase().includes('accepted')
                );
                if (acceptedEntry && acceptedEntry.timestamp) {
                  return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
                }
              }
              return purchase.createdAt?.toDate?.() || new Date(0);
            };
            
            const aDate = getAcceptanceDate(a);
            const bDate = getAcceptanceDate(b);
            return bDate - aDate;
          });
          
          console.log('✅ Completed purchases loaded:', purchases.length);
          setCompletedPurchases(purchases);
          setLoading(false);
          
          // Fetch usernames and profiles for all users in purchases
          if (purchases.length > 0) {
            fetchUsernames(purchases);
            fetchUserProfiles(purchases);
          }
        });
      });
    });
    
    return () => {
      if (unsubscribeBuyer) unsubscribeBuyer();
      if (unsubscribeSeller) unsubscribeSeller();
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, []);

  if (notLoggedIn) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to View Completed Purchases</h2>
          <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to view your completed purchases.</p>
          <Link to="/login" style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-4">Loading completed purchases...</div>;

  return (
    <div className="min-h-screen" onClickCapture={() => saveView('list')} style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Yellow background section */}
      <div
        className="completed-purchases-page"
        style={{
          backgroundColor: '#F7F3EB',
          flex: 1,
          flexDirection: 'column',
          padding: '0 1rem',
          display: 'flex',
        }}
      >
      <div className="completed-purchases-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem', flex: 1 }}>
        <div className={`cp-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
          <style>{`
            .cp-compact-bar-inner {
              max-width: 1100px;
              margin: 0 auto;
              width: 100%;
              box-sizing: border-box;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 12px;
            }
            .cp-compact-title {
              font-family: Nunito, sans-serif;
              font-weight: 700;
              font-size: 1.125rem;
              color: #111827;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              min-width: 0;
              flex: 0 0 auto;
              text-align: left;
            }
            .cp-compact-spacer {
              flex: 1 1 auto;
              min-width: 0;
            }
            .cp-sticky-header .cp-link-btn {
              flex-shrink: 0;
            }
            .cp-sticky-header .cp-header-expanded {
              display: block;
            }
            .cp-sticky-header .cp-mobile-chrome-slot {
              display: none;
            }
            .cp-sticky-header .cp-compact-bar-mobile-fixed {
              display: none;
            }
            @media (min-width: 769px) {
              .cp-sticky-header .cp-compact-bar-mobile-fixed {
                display: none !important;
              }
              .cp-sticky-header .cp-compact-bar {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.7rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s ease;
              }
              .cp-sticky-header.is-compact .cp-compact-bar {
                opacity: 1;
                pointer-events: auto;
              }
              .cp-sticky-header .cp-link-btn {
                padding: 0.6rem 1.4rem !important;
              }
            }
            @media (max-width: 768px) {
              .completed-purchases-page-content {
                padding-top: 0 !important;
              }
              .cp-sticky-header .cp-header-expanded {
                display: none !important;
              }
              .cp-sticky-header .cp-mobile-chrome-slot {
                display: block !important;
                position: relative;
                flex-shrink: 0;
                background-color: #F7F3EB;
                overflow: hidden;
                margin-left: -0.75rem;
                margin-right: -0.75rem;
                width: calc(100% + 1.5rem);
              }
              .cp-sticky-header .cp-compact-bar-inflow {
                display: block;
                padding: 0.6rem 1rem 1.5rem 1rem;
                background-color: #F7F3EB;
                box-sizing: border-box;
              }
              .cp-sticky-header .cp-compact-bar-inflow.is-suppressed {
                visibility: hidden;
                pointer-events: none;
              }
              .cp-sticky-header .cp-compact-bar-desktop {
                display: none !important;
              }
              .cp-sticky-header .cp-compact-bar-mobile-fixed {
                display: block;
              }
              .cp-sticky-header .cp-compact-bar {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.85rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                pointer-events: none;
                transform: translateY(-100%);
                transition: transform 0.2s ease;
              }
              .cp-sticky-header .cp-compact-bar.is-mobile-visible {
                display: block;
                transform: translateY(0);
                pointer-events: auto;
              }
              .cp-sticky-header .cp-compact-title {
                font-size: calc(1rem + 1pt) !important;
                color: #5A9ECB !important;
              }
              .cp-sticky-header .cp-link-btn {
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>

          <div
            ref={mobileChromeSlotRef}
            className="cp-mobile-chrome-slot"
            style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
          >
            <div
              ref={mobileChromeInflowRef}
              className={['cp-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
            >
              {renderMobileChromeBar()}
            </div>
          </div>

          <div
            className={[
              'cp-compact-bar',
              showMobileChromeBar && 'is-mobile-visible',
            ].filter(Boolean).join(' ')}
          >
            <div className="cp-compact-bar-mobile-fixed">
              {renderMobileChromeBar()}
            </div>
            <div className="cp-compact-bar-desktop">
              {renderMobileChromeBar()}
            </div>
          </div>

          <div className="cp-header-expanded">
            <Link
              to="/trading"
              className="completed-purchases-header-back-link"
              style={{
                color: '#5A9ECB',
                textDecoration: 'none',
                alignItems: 'center',
                marginBottom: '1rem',
                fontSize: '1.125rem'
              }}
            >
              ← Back to Trading
            </Link>
            <div className="completed-purchases-header-expanded-inner">
              <div className="completed-purchases-header-copy">
                <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                  Completed Purchases
                </h1>
                <p className="completed-purchases-header-description text-gray-600" style={{ fontSize: '17px' }}>
                  {completedPurchases.length} completed purchase{completedPurchases.length !== 1 ? 's' : ''} • Sorted by most recent
                </p>
              </div>
              <div className="completed-purchases-header-links">
                <Link to="/accepted-purchases" className="cp-link-btn" style={headerLinkBtnStyle}>
                  Accepted Purchases
                </Link>
              </div>
            </div>
          </div>
        </div>

      <style jsx>{`
        @media (min-width: 769px) {
          .completed-purchases-page {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .completed-purchases-header-back-link {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex !important;
            align-items: center;
          }
          .completed-purchases-header-expanded-inner {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem 5rem;
            margin-bottom: 1.5rem;
          }
          .completed-purchases-header-copy {
            flex: 0 1 auto;
            min-width: 0;
          }
          .completed-purchases-header-links {
            flex: 0 0 auto;
            margin-left: auto;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.5rem;
            align-self: flex-end;
          }
          .completed-purchases-header-expanded-inner h1 {
            text-align: left !important;
            margin: 0 !important;
          }
          .completed-purchases-header-description {
            margin: 0 !important;
            padding: 0 !important;
            text-align: left !important;
          }
          .completed-purchases-list {
            max-width: 1100px !important;
            padding: 0 2rem !important;
            box-sizing: border-box !important;
            align-items: stretch !important;
          }
          .completed-purchases-empty {
            padding: 0 2rem !important;
            box-sizing: border-box !important;
          }
          .completed-purchase-card {
            width: 100% !important;
            max-width: none !important;
          }
        }
        @media (max-width: 768px) {
          .completed-purchases-page {
            padding: 0 0.75rem !important;
          }
          .completed-purchases-list {
            padding: 0 !important;
          }
          .completed-purchases-empty {
            padding: 0 !important;
          }
        }
      `}</style>
      {completedPurchases.length === 0 ? (
        <p className='text-gray-600 completed-purchases-empty' style={{ maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>You have no completed purchases.</p>
      ) : (
        <div className="completed-purchases-list" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          {completedPurchases.map((purchase) => {
            const isBuyer = purchase.initiatorId === auth.currentUser?.uid;
            const otherUserId = isBuyer ? purchase.responderId : purchase.initiatorId;
            const otherUsername = usernames[otherUserId] || 'Loading...';
            const userPaid = isBuyer 
              ? purchase.paymentStatus?.buyerPaid 
              : purchase.paymentStatus?.sellerPaid;
            const purchaseAmount = purchase.acceptedAmount || purchase.offerAmount || 0;
            
            const getAcceptanceDate = (purchase) => {
              if (purchase.history && Array.isArray(purchase.history)) {
                const acceptedEntry = [...purchase.history].reverse().find(h => 
                  h.message && h.message.toLowerCase().includes('accepted')
                );
                if (acceptedEntry && acceptedEntry.timestamp) {
                  return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
                }
              }
              return purchase.createdAt?.toDate?.() || new Date();
            };
            
            const purchaseDate = getAcceptanceDate(purchase);
            
            return (
            <div 
              key={purchase.id}
              data-restore-anchor={purchase.id}
              className="completed-purchase-card"
              style={{ 
                borderRadius: '12px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                backgroundColor: 'white',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <style jsx>{`
                @media (max-width: 768px) {
                  .completed-purchase-card {
                    width: 100% !important;
                    padding: 1rem !important;
                    margin: 0 0 1rem 0 !important;
                  }
                  .completed-purchase-pedal-inner {
                    flex-direction: column !important;
                  }
                  .completed-purchase-image {
                    width: 100% !important;
                    max-width: 180px !important;
                    height: auto !important;
                    aspect-ratio: 1 !important;
                  }
                  .completed-purchase-buttons {
                    justify-content: space-between !important;
                    width: 100% !important;
                  }
                  .completed-purchase-buttons a {
                    text-align: center !important;
                  }
                  .completed-purchase-header {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.5rem !important;
                  }
                  .completed-purchase-header > div {
                    justify-content: flex-start !important;
                  }
                  .completed-purchase-date-long {
                    display: none !important;
                  }
                  .completed-purchase-date-short {
                    display: inline !important;
                  }
                }
              `}</style>

              {/* Top row: Date + Status pills */}
              <div className="completed-purchase-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '0.9rem', color: '#6b7280', fontFamily: 'Nunito', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  <span className="completed-purchase-date-long">{purchaseDate ? purchaseDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
                  <span className="completed-purchase-date-short" style={{ display: 'none' }}>{purchaseDate ? purchaseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
                </span>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #C9A227', color: '#946F00', backgroundColor: 'rgba(201, 162, 39, 0.08)' }}>
                    ${typeof purchaseAmount === 'number' ? purchaseAmount.toFixed(0) : purchaseAmount} {isBuyer ? 'purchase' : 'sale'}
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #a7c4a0', color: '#5a7d53', backgroundColor: 'rgba(90, 125, 83, 0.07)' }}>
                    {userPaid ? 'Paid' : 'Unpaid'}
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #86efac', color: '#166534', backgroundColor: 'rgba(22, 101, 52, 0.06)' }}>
                    Completed
                  </span>
                </div>
              </div>

              {/* Pedal row */}
              <div style={{ display: 'flex', alignItems: 'stretch', padding: '1rem 0' }}>
                <div style={{ flex: 1 }}>
                  <div className="completed-purchase-pedal-inner" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {(purchase.targetPedal?.images?.top || placeholderFor(purchase.targetPedal || {}).url) ? (
                      <img
                        src={purchase.targetPedal?.images?.top || placeholderFor(purchase.targetPedal || {}).url}
                        alt='pedal'
                        className="completed-purchase-image"
                        style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#f3f4f6', flexShrink: 0 }}
                      />
                    ) : (
                      <div className="completed-purchase-image" style={{ width: '120px', height: '120px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>No Image</span>
                      </div>
                    )}
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                        {isBuyer ? `Purchased from ${otherUsername}` : `Sold to ${otherUsername}`}
                      </div>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937', fontFamily: 'Nunito' }}>{purchase.targetPedal?.make}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'Nunito' }}>{purchase.targetPedal?.model}</div>
                      <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito', marginTop: '2px' }}>
                        {purchase.targetPedal?.condition} · ${purchase.targetPedal?.price}
                      </div>
                      {purchase.targetPedalId && (
                        <Link
                          to={`/archived-listing/purchase/${purchase.id}/target`}
                          style={{ fontSize: '0.8rem', color: '#5A9ECB', textDecoration: 'none', fontFamily: 'Nunito', display: 'inline-block', marginTop: '4px' }}
                        >
                          View listing →
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom section: Buttons */}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                <div className="completed-purchase-buttons" style={{ display: 'flex', gap: '10px' }}>
                  <Link
                    to={`/purchase-progress/${purchase.id}`}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '9999px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: '#374151',
                      fontWeight: '700',
                      fontSize: '0.9rem',
                      fontFamily: 'Nunito',
                      textDecoration: 'none',
                      display: 'inline-block',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    View progress
                  </Link>
                  {isMessagingAvailable(purchase) && (
                    <Link
                      to={`/purchase-messages/${purchase.id}`}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '9999px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        color: '#374151',
                        fontWeight: '700',
                        fontSize: '0.9rem',
                        fontFamily: 'Nunito',
                        textDecoration: 'none',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Messages
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )})}
        </div>
      )}

      {/* Fixed gap below content */}
      <div style={{ height: '200px' }}></div>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CompletedPurchases;
